import {
  Button,
  DatePicker,
  Input,
  Select,
  Space,
  TableProps,
  Tooltip,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "../../../../../assets/icons/SearchIcon";
import SelectDownIcon from "../../../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../../../assets/icons/ViewIcon";
import StatusItem from "../../../../../components/common/StatusItem";
import CustomTable from "../../../../../components/ui/CustomTable";
import { dbDateFormat } from "../../../../../constants/constants";
import {
  useGeneratePayrollMutation,
  useGetAllKPIBatchQuery,
} from "../../../../../features/organization/contractual/Payroll/payrollApi";
import useError from "../../../../../hooks/useError";
import useTable from "../../../../../hooks/useTable";
import { formatDate } from "../../../../../lib/time";
import { countParPageSerialNumber } from "../../../../../utils/number";
import { capitalize, valueOrNa } from "../../../../../utils/string";
import { addKeyInArray, selectArrayOption } from "../../../../../utils/utils";
import CalanderIcon from "../../../survey/templates/_icons/CalanderIcon";
const { RangePicker } = DatePicker;

const STATUSES = [
  {
    label: "Pending",
    value: "pending",
    className: "status-warning rounded-lg",
  },
  {
    label: "Payroll Generated",
    value: "processed",
    className: "status-link rounded-lg",
  },
];

const KPIBatches = () => {
  const [selectedData, setSelectedData] = useState<(string | number)[]>([]);

  const {
    page,
    limit,
    filter,
    keyword,
    handleLimit,
    handleFilter,
    handleSearch,
    handlePagination,
  } = useTable<any>();

  const { data, isLoading, isFetching } = useGetAllKPIBatchQuery({
    params: { ...filter, search: keyword },
  });

  const [generatePayroll, { isLoading: generating, isError, error }] =
    useGeneratePayrollMutation();

  const handleGeneratePayroll = () => {
    if (!selectedData?.length) return null;
    generatePayroll({ kpi_batch_ids: selectedData });
  };

  useError(isError, error, "Payroll generate failed.");
  const columns: TableProps<any>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      align: "center",
      title: "Cycle",
      dataIndex: "cycle",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Bondhu Number",
      dataIndex: "number_of_user",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Payment Type",
      dataIndex: "payment_type",
      render: (val) => (val ? capitalize(val) : "n/a"),
    },
    {
      align: "center",
      title: "Payment Period",
      render: (record) => (
        <>
          {formatDate(record?.period_from, "DD MMM YYYY")} -{" "}
          {formatDate(record?.period_to)}
        </>
      ),
    },
    {
      align: "center",
      title: "KPI Group",
      dataIndex: "kpi_group_name",
      render: (val) => valueOrNa(val),
    },
    {
      align: "center",
      title: "Payroll Status",
      dataIndex: "payroll_status",
      render: (val) => <StatusItem value={val} list={STATUSES} />,
    },
    {
      title: "Action",
      align: "center",
      render: (record) => (
        <Space align="center">
          <Link
            to={`kpi-details/${record?.id}?cycle=${record?.cycle}&status=${record?.payroll_status}`}
          >
            <Tooltip title="View">
              <Button
                className="action-view"
                size="small"
                icon={<ViewIcon />}
              />
            </Tooltip>
          </Link>
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        page={page}
        limit={limit}
        columns={columns}
        total={data?.data.total}
        handleLimit={handleLimit}
        loading={isLoading || isFetching}
        handlePagination={handlePagination}
        dataSource={addKeyInArray(data?.data.batches)}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys: selectedData,
          onChange: (key) => {
            setSelectedData(key as string[]);
          },
          getCheckboxProps: (record) => {
            return {
              disabled: record?.payroll_status?.toLowerCase() !== "pending",
            };
          },
          columnWidth: 40,
        }}
        filterSection={
          <>
            <Input
              suffix={<SearchIcon />}
              placeholder="Search"
              style={{ minWidth: 170, maxWidth: 180 }}
              onChange={(val) => handleSearch(val)}
              allowClear
            />

            {/* <Select
              allowClear
              placeholder="Payment Type"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={120}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("payment_type", value)}
              options={[
                { label: "Daily", value: "daily" },
                { label: "Weekly", value: "weekly" },
                { label: "Monthly", value: "monthly" },
              ]}
            /> */}
            <RangePicker
              style={{ width: 245 }}
              separator="-"
              className="w-full input-bordered"
              format={"DD/MM/YYYY"}
              suffixIcon={<CalanderIcon />}
              placeholder={["dd/mm/yyyy", "dd/mm/yyyy"]}
              onChange={(dates: (Dayjs | null)[] | null) => {
                const [start, end] = dates || [];

                // Call filter only if both dates are selected
                if (start && end) {
                  handleFilter(
                    "period_from",
                    dayjs(start).format(dbDateFormat)
                  );
                  handleFilter("period_to", dayjs(end).format(dbDateFormat));
                } else {
                  // Clear filters if user clears dates
                  handleFilter("period_from", undefined);
                  handleFilter("period_to", undefined);
                }
              }}
            />

            <Select
              allowClear
              placeholder="Status"
              placement="bottomRight"
              className="min-w-[90px]"
              rootClassName="icon-gray"
              popupMatchSelectWidth={150}
              suffixIcon={<SelectDownIcon />}
              onChange={(value) => handleFilter("status", value)}
              options={selectArrayOption(STATUSES, "label", "value")}
            />

            <Button
              type="primary"
              color="primary"
              loading={generating}
              disabled={!selectedData?.length}
              onClick={() => handleGeneratePayroll()}
            >
              Generate Payroll
            </Button>
          </>
        }
      />
    </>
  );
};

export default KPIBatches;
